import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { DocumentType, EntityType, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';

import { ChangeOrderQuery } from '../state/change-order.query';
import { ChangeOrderService } from '../state/change-order.service';
import { ChangeOrderModel } from '../state/change-order.store';

export interface ChangeOrderUploadData {
  id?: string;
  isVendorDisabled?: boolean;
  vendorId?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ModalComponent,
    TooltipDirective,
    NgSelectComponent,
    FormErrorDirective,
    NgOptionTemplateDirective,
    InputComponent,
    FileManagerComponent,
    NgClass,
    IconComponent,
    AsyncPipe,
  ],
  selector: 'aux-change-order-upload',
  templateUrl: './change-order-upload.component.html',
})
export class ChangeOrderUploadComponent implements OnInit {
  ref = inject(CustomOverlayRef<unknown, ChangeOrderUploadData>);
  vendorQuery = inject(OrganizationQuery);

  private changeDetectorRef = inject(ChangeDetectorRef);
  private changeOrderQuery = inject(ChangeOrderQuery);
  private changeOrderService = inject(ChangeOrderService);
  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(UntypedFormBuilder);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);

  errorMessage = signal('');
  loading = signal(false);

  vendorOptions = computed(() => {
    const allVendors = this.vendorQuery.allVendors();

    return allVendors.map((vendor) => ({
      ...vendor,
      // See: https://github.com/ng-select/ng-select/issues/820
      disabled: !vendor.current_budget_versions?.length,
    }));
  });

  fg = this.formBuilder.group({
    change_order_no: '',
    date_received: '',
    effective_date: '',
    notes: '',
    organization_id: ['', Validators.required],
  });
  _disableVendorsControl = effect(() => {
    const allVendors = this.vendorQuery.allVendors();

    if (!allVendors.length || !this.ref.data?.vendorId) return;

    this.fg.controls['organization_id'].setValue(this.ref.data?.vendorId);

    if (this.isVendorDisabled) this.fg.controls['organization_id'].disable();
  });
  changeOrder!: ChangeOrderModel;
  changeOrderList: ChangeOrderModel[] = [];
  changeOrderNo: string | undefined;
  edit = false;
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  isVendorDisabled = false;
  optionTooltipPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  tooltip = MessagesConstants.CHANGE_ORDER_NOT_SUPPORTED_FOR_VENDOR_CONTRACT_WITHOUT_BUDGET;
  @ViewChild(NgSelectComponent) vendorSelect!: NgSelectComponent;

  private uploaded = false;

  ngOnInit(): void {
    this.fg.controls.organization_id.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();

        if (this.fg.controls.change_order_no.pristine && this.changeOrder) {
          this.fg.controls.change_order_no.markAllAsTouched();
          this.fg.controls.change_order_no.updateValueAndValidity();
        }
      });

    if (this.isVendorDisabled) this.fg.controls['organization_id'].disable();

    this.gqlService
      .listChangeOrders$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ data, errors, success }) =>
        success && data
          ? (this.changeOrderList = data.map(this.changeOrderService.toChangeOrderModel))
          : errors?.length && this.overlayService.error(errors)
      );
  }

  // eslint-disable-next-line complexity
  canDeactivate = async (): Promise<boolean> => {
    const { change_order_no, date_received, effective_date, notes, organization_id } =
      this.fg.getRawValue();
    const hasChanges =
      (change_order_no === ''
        ? undefined
        : change_order_no !== this.changeOrder?.change_order_no) ||
      (notes === '' ? undefined : notes !== this.changeOrder?.notes) ||
      (effective_date === '' ? undefined : effective_date !== this.changeOrder?.effective_date) ||
      (date_received === '' ? undefined : date_received !== this.changeOrder?.date_received) ||
      (organization_id === (this.ref.data?.vendorId ? this.ref.data?.vendorId : '')
        ? undefined
        : organization_id !== this.changeOrder?.organization_id);

    if (hasChanges && !this.uploaded) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event?.data;
    }

    return true;
  };

  get changeOrderNoValidators(): ValidatorFn[] {
    const changeOrderNumbersToForbid = this.changeOrderList
      .filter(
        (changeOrder) => changeOrder.organization_id === this.fg.controls.organization_id.value
      )
      .map((changeOrder) => changeOrder.change_order_no)
      .filter(
        (changeOrderNo) =>
          !this.loading() &&
          (changeOrderNo !== this.changeOrderNo ||
            !this.changeOrder ||
            this.changeOrder.organization_id !== this.fg.controls.organization_id.value)
      );

    return [
      Validators.required,
      CustomValidators.valueNotInList(changeOrderNumbersToForbid, 'duplicatedChangeOrder'),
    ];
  }

  constructor() {
    this.organizationService.getListWithTotalBudgetAmount().pipe(takeUntilDestroyed()).subscribe();

    if (this.ref.data?.id) {
      const co = this.changeOrderQuery.getEntity(this.ref.data.id);

      if (co) {
        this.changeOrder = co;
        this.changeOrderNo = co.change_order_no;
        this.fg.patchValue({
          change_order_no: co.change_order_no,
          date_received: co.date_received,
          effective_date: co.effective_date,
          notes: co.notes,
          organization_id: co.organization_id,
        });

        this.edit = true;
      }
    }

    if (this.ref.data?.isVendorDisabled) this.isVendorDisabled = this.ref.data.isVendorDisabled;

    this.ref.canDeactivate = this.canDeactivate;
  }

  getFilePath(org_id: string, co_id: string) {
    const trial_id = this.mainQuery.getValue().trialKey;

    return `trials/${trial_id}/vendors/${org_id}/changeorders/${co_id}/`;
  }

  // eslint-disable-next-line max-lines-per-function
  async onUpload() {
    if (!this.fileManager) {
      return;
    }

    this.fg.markAllAsTouched();
    this.fg.updateValueAndValidity();
    this.fg.controls.change_order_no.updateValueAndValidity();

    const isFileNotExist = this.fileManager.fileQuery.getCount() === 0;

    if (!this.edit) {
      if (isFileNotExist) {
        this.errorMessage.set('You must upload a file in order to continue.');
      } else {
        this.errorMessage.set('');
      }
    }

    if (this.fg.invalid || this.loading() || (this.edit ? false : isFileNotExist)) {
      return;
    }

    this.loading.set(true);
    const { change_order_no, date_received, effective_date, notes, organization_id } =
      this.fg.getRawValue();

    let id = '';

    if (this.edit) {
      id = this.changeOrder.id;
      const { data, errors, success } = await this.changeOrderService.update({
        change_order_no,
        date_received,
        effective_date,
        id: this.changeOrder.id,
        notes,
        organization_id,
      });

      if (!(success && data)) {
        this.overlayService.error(errors, undefined, true);
      }
    } else {
      const { data, errors, success } = await this.changeOrderService.add({
        change_order_no,
        date_received,
        effective_date,
        notes,
        organization_id,
      });

      if (success && data) {
        id = data.id;
      } else {
        this.overlayService.error(errors, undefined, true);
      }
    }

    if (id) {
      const files = this.fileManager.fileQuery.getAll();

      for (const file of files) {
        this.fileManager.fileStore.update(file.id, {
          ...file,
          bucket_key: `${this.getFilePath(organization_id, id)}${file.bucket_key}`,
        });
      }

      const filesSuccess = await this.fileManager.fileService.uploadFiles({
        insertDocument: true,
        metadata: {
          documentType: DocumentType.DOCUMENT_CHANGE_ORDER_SUPPORT,
          entity_id: id,
          entity_type_id: EntityType.CHANGE_ORDER,
          status: 'Pending Review',
          vendor: organization_id,
        },
        showSuccess: false,
      });

      if (filesSuccess) {
        if (this.edit) {
          this.overlayService.success(`Change Order successfully updated!`);
        } else {
          this.overlayService.success(`Change Order successfully created!`);
        }

        this.uploaded = true;
        this.ref.close(true);
      }
    }
  }

  removeFile(file: File) {
    if (this.fileManager) {
      void this.fileManager.removeFile(file);
    }
  }
}
